import { useEffect } from 'react';
import { connect } from 'react-redux';
import { Link, RouteComponentProps, withRouter } from 'react-router-dom';

import { Button } from 'components/UtilElements';
import { Dispatch } from 'redux';
import { UPDATE_SELECTED_CHILD_COURSE_ID, UPDATE_SELECTED_PARENT_COURSE_ID } from 'redux/actions';
import { RootState } from 'redux/reducers';
import { ROUTES } from 'routes';

interface ParentCourseRow {
  isParent: true;
}

interface ChildCourseRow {
  isParent: false;
  childId: string;
}

type CourseRowProps = (ParentCourseRow | ChildCourseRow) & {
  courseId: string;
  code: string;
  title: string;
  isImported: NumericBoolean;
  updateSelectedParentCourseId: (parentCourseId: string) => void;
  updateSelectedChildCourseId: (childCourseId: string) => void;
};

const CourseRow = ({
  courseId,
  code,
  title,
  isImported,
  updateSelectedParentCourseId,
  updateSelectedChildCourseId,
  ...props
}: CourseRowProps) => {
  return (
    <tr>
      <td className='table__cell table__cell--left'>{code}</td>
      <td className='table__cell table__cell--left'>{title}</td>
      <td className='table__cell table__cell--left'>
        {isImported ? (
          'Imported'
        ) : (
          <Link
            to={{ pathname: ROUTES.ADMIN_COURSES_CREATE, state: { courseId } }}
            component={({ navigate, href }) => {
              return (
                <Button
                  variant='outlined'
                  color='info'
                  size='small'
                  className='import-course__action'
                  onClick={() => {
                    updateSelectedParentCourseId(courseId);
                    if (!props.isParent) updateSelectedChildCourseId(props.childId);
                    navigate(href);
                  }}
                >
                  IMPORT
                </Button>
              );
            }}
          />
        )}
      </td>
    </tr>
  );
};

const mapStateToCourseRowProps = () => ({});

const mapDispatchToCourseRowProps = (dispatch: Dispatch) => ({
  updateSelectedParentCourseId: (parentCourseId: string) => {
    dispatch({
      type: UPDATE_SELECTED_PARENT_COURSE_ID,
      payload: parentCourseId,
    });
  },
  updateSelectedChildCourseId: (childCourseId: string) => {
    dispatch({
      type: UPDATE_SELECTED_CHILD_COURSE_ID,
      payload: childCourseId,
    });
  },
});

const ConnectedCourseRow = connect(mapStateToCourseRowProps, mapDispatchToCourseRowProps)(CourseRow);

interface ParentCourseProps {
  courseDetails: SessionData['courseDetails'] | undefined;
}

const ParentCourse = ({ courseDetails }: ParentCourseProps) => {
  if (!courseDetails) return null;

  const { courseId, code, title, isImported } = courseDetails;

  return (
    <div className='section'>
      <div className='section__header'>
        <h3 className='section__title'>Import the course as just one course</h3>
      </div>
      <div className='section__body'>
        <table className='table table--bordered'>
          <thead>
            <tr>
              <th className='table__cell table__cell--left table__cell--course-code'>Course Code</th>
              <th className='table__cell table__cell--left  table__cell--course-title'>Title</th>
              <th className='table__cell  table__cell--course-actions' />
            </tr>
          </thead>
          <tbody>
            <ConnectedCourseRow
              isParent
              courseId={courseId}
              code={code}
              title={title}
              isImported={isImported}
            />
          </tbody>
        </table>
      </div>
    </div>
  );
};

interface ChildCoursesProps {
  courseDetails: SessionData['courseDetails'] | undefined;
}

const ChildCourses = ({ courseDetails }: ChildCoursesProps) => {
  if (!courseDetails) return null;

  const { courseId, hasChildren, childCourses } = courseDetails;

  if (hasChildren === 0) return null;

  return (
    <div className='section'>
      <div className='section__header'>
        <h3 className='section__title'>Import each child course as a separate course</h3>
      </div>
      <div className='section__body'>
        <table className='table table--bordered'>
          <thead>
            <tr>
              <th className='table__cell table__cell--left table__cell--course-code'>Course Code</th>
              <th className='table__cell table__cell--left table__cell--course-title'>Title</th>
              <th className='table__cell table__cell--course-actions' />
            </tr>
          </thead>
          <tbody>
            {childCourses.map(({ childId, code, title, isImported }) => (
              <ConnectedCourseRow
                key={childId}
                isParent={false}
                courseId={courseId}
                childId={childId}
                code={code}
                title={title}
                isImported={isImported}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

interface RouteState {
  courseId: string;
}

export interface SelectAdminCourseProps extends RouteComponentProps<never, never, RouteState> {
  allCourses: AvailableCourse[];
}

const SelectAdminCourse = ({ allCourses, history, location }: SelectAdminCourseProps) => {
  const { courseId } = location.state;
  const courseDetails = allCourses.find((course) => course.courseId === courseId);

  useEffect(() => {
    if (!courseDetails) {
      history.push(ROUTES.ADMIN_COURSES);
      return;
    }

    if (courseDetails.isImported === 0) return;

    if (courseDetails.hasChildren === 1) {
      const isAnyChildNotImported = courseDetails.childCourses.some(
        (childCourse) => childCourse.isImported === 0
      );
      if (isAnyChildNotImported) return;
    }

    history.push(ROUTES.ADMIN_COURSES);
  }, [history, courseId, courseDetails]);

  return (
    <div className='page select-course'>
      <div className='select-course-header'>
        <h1 className='header'>Importing course into Acadly</h1>
        <Link
          to={{
            pathname: ROUTES.ADMIN_COURSES,
          }}
          component={({ navigate, href }) => {
            return (
              <Button
                variant='text'
                color='info'
                size='small'
                className='import-course__action'
                onClick={() => {
                  navigate(href);
                }}
              >
                CLOSE
              </Button>
            );
          }}
        />
      </div>
      <div className='page__body'>
        <ParentCourse courseDetails={courseDetails} />
        <ChildCourses courseDetails={courseDetails} />
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  allCourses: state.allCourses,
});

export default withRouter(connect(mapStateToProps)(SelectAdminCourse));
